import { useQuery, useMutation } from "@tanstack/react-query";
import { api, API_ROOT } from "@/lib/api";
import { useState, useMemo } from "react";
import { Sparkles, ShieldCheck, Radar, X, ArrowRight, Wand2 } from "lucide-react";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";

const CATEGORIES = [
    { key: "", label: "Tutte" },
    { key: "ophthalmology", label: "Oftalmologia" },
    { key: "eye_pathology", label: "Patologie oculari" },
    { key: "general_medicine", label: "Medicina generale" },
    { key: "prevention", label: "Prevenzione" },
    { key: "pediatric_ophthalmology", label: "Pediatria" },
    { key: "accessibility", label: "Accessibilità" },
    { key: "privacy_health", label: "Privacy sanitaria" },
    { key: "social", label: "Sociale" },
];

const CAT_COLOR = {
    ophthalmology: "bg-primary/10 text-primary",
    eye_pathology: "bg-electric/10 text-electric",
    general_medicine: "bg-emerald-500/10 text-emerald-600",
    prevention: "bg-azure/10 text-azure",
    accessibility: "bg-opportunity/10 text-opportunity",
    privacy_health: "bg-warning/10 text-warning",
    pediatric_ophthalmology: "bg-pink-500/10 text-pink-500",
    social: "bg-textsubtle/10 text-textmuted",
};

export default function HealthCalendarPage() {
    const [category, setCategory] = useState("");
    const [officialOnly, setOfficialOnly] = useState(false);
    const [selected, setSelected] = useState(null);
    const year = new Date().getFullYear();

    const q = useQuery({
        queryKey: ["health-calendar", year, category, officialOnly],
        queryFn: () => api.get(`/health-calendar?year=${year}&category=${category}&officialOnly=${officialOnly}`).then((r) => r.data),
    });

    const items = q.data || [];
    const byMonth = useMemo(() => {
        const g = {};
        items.forEach((it) => {
            if (!it.resolved_date) return;
            const m = it.resolved_date.slice(0, 7);
            g[m] = g[m] || [];
            g[m].push(it);
        });
        return g;
    }, [items]);

    return (
        <div className="space-y-6 pv-enter">
            <div className="flex items-end justify-between">
                <div>
                    <div className="pv-eyebrow mb-2">Marketing · Ricorrenze Sanitarie</div>
                    <h1 className="pv-serif text-4xl tracking-tight">Health & Eye Care Calendar</h1>
                    <p className="text-[13.5px] text-textmuted mt-2 max-w-2xl">
                        Giornate mondiali OMS/ONU, campagne IAPB, World Glaucoma Association e AAO.
                        Ogni ricorrenza è pronta per diventare una campagna multicanale.
                    </p>
                </div>
                <div className="pv-mono text-[11px] text-textsubtle">Anno {year}</div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1 rounded-xl border border-border/[var(--border-opacity)] bg-surface p-1 overflow-x-auto pv-scroll">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c.key}
                            onClick={() => setCategory(c.key)}
                            className={`text-[11.5px] px-2.5 py-1 rounded-lg whitespace-nowrap transition-colors ${
                                category === c.key ? "bg-primary/[0.10] text-primary" : "text-textmuted hover:text-textmain"
                            }`}
                        >{c.label}</button>
                    ))}
                </div>
                <button
                    onClick={() => setOfficialOnly((v) => !v)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11.5px] border transition-colors ${
                        officialOnly ? "border-primary bg-primary/[0.06] text-primary" : "border-border/[var(--border-opacity)] text-textmuted"
                    }`}
                >
                    <ShieldCheck size={12} strokeWidth={1.5} /> Solo ufficiali
                </button>
            </div>

            <div className="space-y-6">
                {Object.entries(byMonth).map(([m, arr]) => (
                    <div key={m}>
                        <div className="pv-eyebrow mb-3">{new Date(m + "-01").toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</div>
                        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                            {arr.map((r) => (
                                <button
                                    key={r.id}
                                    data-testid={TID.healthRecurrence(r.id)}
                                    onClick={() => setSelected(r)}
                                    className="pv-card pv-hover p-5 text-left"
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <span className={`text-[10.5px] px-2 py-0.5 rounded-md uppercase tracking-wider font-medium ${CAT_COLOR[r.category] || "bg-textsubtle/10 text-textmuted"}`}>
                                            {r.type}
                                        </span>
                                        {r.official && <ShieldCheck size={13} strokeWidth={1.5} className="text-success" />}
                                    </div>
                                    <div className="text-[15px] font-medium leading-snug">{r.name_it}</div>
                                    <div className="text-[11.5px] text-textsubtle mt-0.5">{r.name_en}</div>
                                    <div className="flex items-center justify-between mt-3">
                                        <div className="text-[12px] text-textmuted pv-mono">
                                            {new Date(r.resolved_date).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}
                                        </div>
                                        <div className="text-[11px] text-textsubtle">Rilevanza {r.relevance}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {selected && <RecurrenceDrawer rec={selected} onClose={() => setSelected(null)} />}
        </div>
    );
}

function RecurrenceDrawer({ rec, onClose }) {
    const [campaign, setCampaign] = useState(null);
    const [busy, setBusy] = useState(false);

    const gen = async () => {
        setBusy(true);
        setCampaign(null);
        try {
            const token = localStorage.getItem("pv_token");
            const res = await fetch(`${API_ROOT}/ai/generate-campaign/${rec.id}`, {
                method: "POST",
                headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                credentials: "include",
            });
            if (!res.ok || !res.body) throw new Error("Errore generazione");
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let sseBuffer = "";
            let finalCampaign = null;
            let errored = false;
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                sseBuffer += decoder.decode(value, { stream: true });
                let idx;
                while ((idx = sseBuffer.indexOf("\n\n")) !== -1) {
                    const raw = sseBuffer.slice(0, idx);
                    sseBuffer = sseBuffer.slice(idx + 2);
                    if (raw.startsWith(":")) continue; // keep-alive comment
                    let eventName = "message";
                    let dataStr = "";
                    for (const line of raw.split("\n")) {
                        if (line.startsWith("event: ")) eventName = line.slice(7).trim();
                        else if (line.startsWith("data: ")) dataStr += line.slice(6);
                    }
                    if (eventName === "campaign" && dataStr) {
                        try { finalCampaign = JSON.parse(dataStr); } catch {}
                    } else if (eventName === "error") {
                        errored = true;
                    }
                }
            }
            if (errored || !finalCampaign) throw new Error("AI error");
            setCampaign(finalCampaign);
            toast.success("Campagna generata da Prime Vision AI.");
        } catch (e) {
            toast.error("Errore generazione. Riprova.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full sm:w-[540px] h-full flex flex-col pv-glass border-l border-border/[var(--border-opacity)] shadow-2xl pv-enter">
                <div className="flex items-center justify-between px-6 py-4 border-b border-border/[var(--border-opacity)]">
                    <div className="pv-eyebrow flex items-center gap-2"><Radar size={11} strokeWidth={1.5} />Ricorrenza</div>
                    <button onClick={onClose} className="h-7 w-7 rounded-lg hover:bg-surface2 flex items-center justify-center text-textmuted">
                        <X size={15} strokeWidth={1.5} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto pv-scroll p-6 space-y-5">
                    <div>
                        <h2 className="pv-serif text-3xl leading-tight tracking-tight">{rec.name_it}</h2>
                        <div className="text-[13px] text-textmuted">{rec.name_en}</div>
                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                            <span className={`text-[10.5px] px-2 py-0.5 rounded-md ${CAT_COLOR[rec.category] || "bg-textsubtle/10 text-textmuted"}`}>{rec.type}</span>
                            {rec.official && <span className="text-[10.5px] px-2 py-0.5 rounded-md bg-success/10 text-success flex items-center gap-1"><ShieldCheck size={11} strokeWidth={1.5} />Ufficiale</span>}
                            <span className="text-[10.5px] px-2 py-0.5 rounded-md bg-primary/[0.08] text-primary pv-mono">
                                {new Date(rec.resolved_date).toLocaleDateString("it-IT")}
                            </span>
                        </div>
                    </div>

                    <div className="text-[13.5px] leading-relaxed text-textmain">{rec.description}</div>

                    <div className="grid grid-cols-2 gap-4 text-[12.5px]">
                        <F label="Fonte" v={rec.source} />
                        <F label="Affidabilità" v={rec.reliability} />
                        <F label="Tema ufficiale" v={rec.official_theme || "—"} />
                        <F label="Hashtag" v={rec.official_hashtag || "—"} />
                        <F label="Pubblico" v={rec.audience} />
                        <F label="Rilevanza" v={`${rec.relevance}/100`} />
                    </div>

                    <div className="pv-card p-4 bg-primary/[0.03]">
                        <div className="pv-eyebrow mb-1.5">Collegamento con Prime Vision</div>
                        <div className="text-[13px] leading-relaxed">{rec.prime_vision_link}</div>
                    </div>

                    <div className="pv-card p-4">
                        <div className="pv-eyebrow mb-1.5">Opportunity Score</div>
                        <div className="flex items-baseline gap-2">
                            <div className="pv-serif text-5xl text-primary">{rec.opportunity_score ?? Math.round(rec.relevance * 0.9)}</div>
                            <div className="text-[11px] text-textsubtle">/ 100</div>
                        </div>
                        <div className="mt-2 text-[12px] text-textmuted">
                            {(rec.opportunity_score ?? rec.relevance) >= 80 ? "Campagna prioritaria." :
                             (rec.opportunity_score ?? rec.relevance) >= 60 ? "Contenuto consigliato." :
                             "Opportunità secondaria."}
                        </div>
                    </div>

                    <button
                        onClick={gen}
                        disabled={busy}
                        data-testid={TID.healthGenerateCampaign}
                        className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl py-2.5 text-[13.5px] font-medium hover:opacity-90 disabled:opacity-60 transition-opacity"
                    >
                        <Wand2 size={14} strokeWidth={1.5} className={busy ? "animate-spin" : ""} />
                        {busy ? "Prime Vision AI sta generando…" : "Genera campagna multicanale"}
                    </button>

                    {campaign && (
                        <div className="space-y-3">
                            <CampaignBlock title="Instagram" icon="📸">
                                <div className="text-[12.5px] mb-1"><b>Reel:</b> {campaign.instagram?.reel_script}</div>
                                <div className="text-[12.5px] mb-1"><b>Caption:</b> {campaign.instagram?.caption}</div>
                                <div className="text-[11px] text-textmuted">{campaign.instagram?.hashtags}</div>
                            </CampaignBlock>
                            <CampaignBlock title="LinkedIn">
                                <div className="text-[12.5px]">{campaign.linkedin?.post}</div>
                            </CampaignBlock>
                            <CampaignBlock title="Newsletter">
                                <div className="text-[12.5px] mb-1"><b>Oggetto:</b> {campaign.newsletter?.subject}</div>
                                <div className="text-[12.5px]">{campaign.newsletter?.body}</div>
                            </CampaignBlock>
                            <CampaignBlock title="WhatsApp">
                                <div className="text-[12.5px]">{campaign.whatsapp?.short}</div>
                            </CampaignBlock>
                            <CampaignBlock title="Webinar">
                                <div className="text-[12.5px] mb-1"><b>{campaign.webinar?.title}</b></div>
                                <div className="text-[12.5px] text-textmuted">{campaign.webinar?.objective}</div>
                            </CampaignBlock>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function F({ label, v }) {
    return (
        <div>
            <div className="pv-eyebrow mb-0.5">{label}</div>
            <div className="text-textmain leading-snug">{v}</div>
        </div>
    );
}

function CampaignBlock({ title, icon, children }) {
    return (
        <div className="pv-card p-4">
            <div className="pv-eyebrow mb-2 flex items-center gap-1.5">{icon} {title}</div>
            {children}
        </div>
    );
}
